import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { underwritingService } from "@/services/UnderwritingService";
import {
  emptyAdditionalLenderQuestions,
  getAdditionalQuestionErrors,
} from "@/helpers/additionalLenderQuestions";
import {
  existingLenderOptions,
  MAX_COMPANY_ROWS,
  nationalityOptions,
  occupationSectorOptions,
  OTHER_LENDER_OPTION,
  vwfsOccupationSectorOptions,
  VWFS_DISCLAIMER,
  VWFS_PRIVACY_POLICY_URL,
} from "@/constants/additionalQuestions";
import type {
  AdditionalLenderQuestions,
  AdditionalQuestionCompany,
} from "@/models/underwriting";

interface AdditionalQuestionsPageProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: number | null;
  /** Lender of the quote being proposed — shown on the confirm button. */
  lenderName: string | null;
  /** True when the generated quotes include a VWFS lender, which adds their question set. */
  showVwfs: boolean;
  /** Years in the customer's current employment — under 3 VWFS also ask for the previous sector. */
  employmentYears: number | null;
  /** Called once the answers are stored. */
  onSubmitted: () => void;
}

type Field = keyof AdditionalLenderQuestions;

// Layout primitives

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card p-3 shadow-sm">
      <h3 className="mb-3 text-[12px] font-semibold text-foreground">{title}</h3>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

function Row({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: ReactNode;
  hint?: string;
  error?: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor} className="text-[12px] font-medium leading-snug text-foreground">
        {label}
        {hint && <span className="font-normal text-muted-foreground"> ({hint})</span>}
      </Label>
      {children}
      {error && <p className="text-[11px] text-rose-600">{error}</p>}
    </div>
  );
}

function YesNo({
  value,
  onChange,
  labels = ["Yes", "No"],
  invalid,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
  labels?: [string, string];
  invalid?: boolean;
}) {
  return (
    <div className={cn("flex gap-2", invalid && "rounded-md ring-1 ring-rose-500")}>
      {([true, false] as const).map((option, i) => (
        <Button
          key={labels[i]}
          type="button"
          size="sm"
          variant={value === option ? "default" : "outline"}
          className={cn("h-8 px-4 text-[12px]", value === option && "bg-purple-600 text-white hover:bg-purple-700")}
          aria-pressed={value === option}
          onClick={() => onChange(option)}
        >
          {labels[i]}
        </Button>
      ))}
    </div>
  );
}

function CompanyRows({
  rows,
  nameLabel,
  numberLabel,
  onChange,
}: {
  rows: AdditionalQuestionCompany[];
  nameLabel: string;
  numberLabel: string;
  onChange: (rows: AdditionalQuestionCompany[]) => void;
}) {
  // The form always shows at least one row so the agent has somewhere to type.
  const visible = rows.length ? rows : [{ companyName: "", companyNumber: "" }];

  const setField = (index: number, key: keyof AdditionalQuestionCompany, raw: string) => {
    const next = visible.map((row, i) => (i === index ? { ...row, [key]: raw.slice(0, 100) } : row));
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      {visible.map((row, index) => (
        <div key={index} className="flex flex-wrap items-end gap-2">
          <div className="min-w-[160px] flex-1">
            <Label className="text-[11px] text-muted-foreground">{nameLabel}</Label>
            <Input
              className="mt-1"
              value={row.companyName}
              onChange={(e) => setField(index, "companyName", e.target.value)}
            />
          </div>
          <div className="min-w-[160px] flex-1">
            <Label className="text-[11px] text-muted-foreground">{numberLabel}</Label>
            <Input
              className="mt-1"
              value={row.companyNumber}
              onChange={(e) => setField(index, "companyNumber", e.target.value)}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9"
            disabled={visible.length <= 1}
            onClick={() => onChange(visible.filter((_, i) => i !== index))}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={visible.length >= MAX_COMPANY_ROWS}
        onClick={() => onChange([...visible, { companyName: "", companyNumber: "" }])}
      >
        Add company
      </Button>
    </div>
  );
}

export function AdditionalQuestionsPage({
  open,
  onOpenChange,
  applicationId,
  lenderName,
  showVwfs,
  employmentYears,
  onSubmitted,
}: AdditionalQuestionsPageProps) {
  const [form, setForm] = useState<AdditionalLenderQuestions>(emptyAdditionalLenderQuestions);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [customLender, setCustomLender] = useState(false);

  const showPreviousOccupation = (employmentYears ?? 0) < 3;

  // Tracks which application's answers are loaded so going Back and reopening (e.g.
  // re-proposing the same quote) keeps in-progress edits instead of refetching over them.
  const loadedForAppId = useRef<number | null>(null);

  useEffect(() => {
    if (!open || applicationId == null) return;
    if (loadedForAppId.current === applicationId) return;
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    setErrors({});
    setSaveError(null);
    underwritingService
      .getAdditionalLenderQuestions(applicationId, controller.signal)
      .then((saved) => {
        const next = saved ?? emptyAdditionalLenderQuestions();
        setForm(next);
        setCustomLender(
          !!next.existingLender && !existingLenderOptions.includes(next.existingLender)
        );
        loadedForAppId.current = applicationId;
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setForm(emptyAdditionalLenderQuestions());
          setLoadError("Couldn't load the saved answers. Fill them in below to continue.");
          loadedForAppId.current = applicationId;
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, applicationId]);

  const set = useCallback(<K extends Field>(key: K, value: AdditionalLenderQuestions[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const setNumber = useCallback(
    (key: Field, raw: string) => {
      const value = raw === "" ? null : Number(raw);
      if (value !== null && Number.isNaN(value)) return;
      set(key, value as AdditionalLenderQuestions[typeof key]);
    },
    [set]
  );

  const handleReplacementLoan = (yes: boolean) => {
    setErrors({});
    setCustomLender(false);
    setForm((prev) => ({
      ...prev,
      replacementLoan: yes,
      replacementLoanAmount: yes ? prev.replacementLoanAmount : null,
      existingLender: yes ? prev.existingLender : null,
      replacementLoanMonthlyPayment: yes ? prev.replacementLoanMonthlyPayment : null,
    }));
  };

  const handleCompanyToggle = (key: "hasMajorityShareholdingInterest" | "isPartnerOfPartnership") =>
    (yes: boolean) => {
      const rowsKey = key === "hasMajorityShareholdingInterest" ? "shareholdingCompanies" : "partnershipCompanies";
      setForm((prev) => ({
        ...prev,
        [key]: yes,
        [rowsKey]: yes
          ? prev[rowsKey].length
            ? prev[rowsKey]
            : [{ companyName: "", companyNumber: "" }]
          : [],
      }));
      setErrors({});
    };

  const save = useCallback(async () => {
    if (applicationId == null) return;
    setSaving(true);
    setSaveError(null);
    try {
      await underwritingService.saveAdditionalLenderQuestions(applicationId, form);
      onSubmitted();
    } catch {
      setSaveError("The additional questions couldn't be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [applicationId, form, onSubmitted]);

  const handleSubmit = () => {
    const found = getAdditionalQuestionErrors(form, showVwfs, showPreviousOccupation);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    save();
  };

  const money = (key: Field, label: string, hint?: string) => (
    <Row label={label} hint={hint} error={errors[key]} htmlFor={`aq-${key}`}>
      <div className="relative w-48">
        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          £
        </span>
        <Input
          id={`aq-${key}`}
          type="number"
          min={0}
          className={cn("pl-6", errors[key] && "border-rose-500")}
          placeholder="0.00"
          value={form[key] == null ? "" : String(form[key])}
          onChange={(e) => setNumber(key, e.target.value)}
        />
      </div>
    </Row>
  );

  const stringSelect = (key: Field, label: string, options: string[], placeholder: string) => (
    <Row label={label} error={errors[key]}>
      <Select
        value={(form[key] as string | null) ?? ""}
        onValueChange={(v) => set(key, v as AdditionalLenderQuestions[typeof key])}
      >
        <SelectTrigger className={cn("w-full", errors[key] && "border-rose-500")}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Row>
  );

  const vwfsAgreement = (key: Field, label: string) => (
    <Row label={label} error={errors[key]}>
      <YesNo
        value={form[key] as boolean | null}
        onChange={(v) => set(key, v as AdditionalLenderQuestions[typeof key])}
        labels={["Agree", "Disagree"]}
        invalid={!!errors[key]}
      />
    </Row>
  );

  const errorCount = useMemo(() => Object.keys(errors).length, [errors]);

  if (!open) return null;

  return (
    <div
      role="region"
      aria-label="Additional Questions"
      className="flex flex-col rounded-xl border border-border bg-card shadow-sm"
    >
      <header className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 px-2 text-xs"
            disabled={saving}
            onClick={() => onOpenChange(false)}
          >
            <ArrowLeft className="size-4" />
            Back
          </Button>
          <div>
            <h2 className="text-sm font-semibold text-foreground">Additional Questions</h2>
            <p className="text-[12px] text-muted-foreground">
              These answers are submitted with the proposal
              {lenderName ? ` to ${lenderName}` : ""}. Complete them to continue.
            </p>
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-4 p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading answers…
          </div>
        ) : (
          <>
            {loadError && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                {loadError}
              </p>
            )}

            <div className="flex flex-col gap-3">
              <Section title="Income">
                {money("grossAnnualIncome", "Gross Annual Income")}
                {money("additionalAnnualIncome", "Additional Annual Income")}
              </Section>

              <Section title="Monthly Outgoings">
                {money("rentMortgageAmount", "Rent/Mortgage")}
                {money("utilitiesHouseholdPayments", "Utilities / Household Bills")}
                {money("insuranceSubscriptionPayments", "Insurance / Subscription Payments")}
                {money("creditCardLoanPayments", "Credit Card / Loan Payments", "Optional")}
                <Row
                  label="What is the total number of dependent children living in the household?"
                  error={errors.dependents}
                  htmlFor="aq-dependents"
                >
                  <Input
                    id="aq-dependents"
                    type="number"
                    min={0}
                    className={cn("w-48", errors.dependents && "border-rose-500")}
                    placeholder="0"
                    value={form.dependents == null ? "" : String(form.dependents)}
                    onChange={(e) => setNumber("dependents", e.target.value)}
                  />
                </Row>
                {stringSelect(
                  "occupationSector",
                  "Occupation Sector",
                  occupationSectorOptions,
                  "Select an option"
                )}
              </Section>

              <Section title="Driving Licence">
                {stringSelect(
                  "nationalityOnLicence",
                  "Nationality on Licence",
                  nationalityOptions,
                  "Select nationality"
                )}
                <Row label="Driving Licence Number" hint="Optional" htmlFor="aq-licence">
                  <Input
                    id="aq-licence"
                    value={form.drivingLicenceNumber ?? ""}
                    onChange={(e) => set("drivingLicenceNumber", e.target.value || null)}
                  />
                </Row>
              </Section>

              <Section title="Other Questions">
                <Row label="Is this a replacement loan?" error={errors.replacementLoan}>
                  <YesNo
                    value={form.replacementLoan}
                    onChange={handleReplacementLoan}
                    invalid={!!errors.replacementLoan}
                  />
                </Row>

                {form.replacementLoan === true && (
                  <>
                    {money("replacementLoanAmount", "Replacement Loan Amount")}
                    <Row label="Existing Lender" error={errors.existingLender}>
                      <Select
                        value={
                          customLender ? OTHER_LENDER_OPTION : form.existingLender ?? ""
                        }
                        onValueChange={(v) => {
                          const isOther = v === OTHER_LENDER_OPTION;
                          setCustomLender(isOther);
                          set("existingLender", isOther ? null : v);
                        }}
                      >
                        <SelectTrigger
                          className={cn("w-full", errors.existingLender && "border-rose-500")}
                        >
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          {[...existingLenderOptions, OTHER_LENDER_OPTION].map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Row>
                    {customLender && (
                      <Row label="Lender Name" htmlFor="aq-custom-lender">
                        <Input
                          id="aq-custom-lender"
                          placeholder="Enter lender name"
                          value={form.existingLender ?? ""}
                          onChange={(e) => set("existingLender", e.target.value || null)}
                        />
                      </Row>
                    )}
                    {money("replacementLoanMonthlyPayment", "Replacement Loan Monthly Payment")}
                  </>
                )}

                <Row label="Is this vehicle for business use?" hint="Optional">
                  <YesNo value={form.businessUse} onChange={(v) => set("businessUse", v)} />
                </Row>

                <Row
                  label="Are there any foreseeable changes in the customer's circumstances?"
                  error={errors.circumstancesChange}
                >
                  <YesNo
                    value={form.circumstancesChange}
                    onChange={(v) => set("circumstancesChange", v)}
                    invalid={!!errors.circumstancesChange}
                  />
                </Row>
                {form.circumstancesChange === true && (
                  <Row
                    label="Reason for change in circumstances"
                    error={errors.circumstancesChangeReason}
                    htmlFor="aq-change-reason"
                  >
                    <Input
                      id="aq-change-reason"
                      placeholder="Enter reason"
                      className={cn(errors.circumstancesChangeReason && "border-rose-500")}
                      value={form.circumstancesChangeReason ?? ""}
                      onChange={(e) => set("circumstancesChangeReason", e.target.value || null)}
                    />
                  </Row>
                )}
              </Section>

              {showVwfs && (
                <Section title="Volkswagen Financial Services (UK) Limited Questions">
                  {vwfsAgreement(
                    "affordFinanceWithCurrentIncome",
                    "I can afford the finance with my current levels of income and expenditure"
                  )}
                  {vwfsAgreement(
                    "incomeNotExpectedToChange",
                    "My income is not expected to change in a way which could impact my ability to maintain repayments"
                  )}
                  {vwfsAgreement(
                    "expenditureNotExpectedToChange",
                    "My expenditure is not expected to change in a way which could impact my ability to maintain repayments"
                  )}

                  <Row label="Volkswagen Financial Services (UK) Limited would like to send you information about its products and services. If you agree, please select how you would like to be contacted:">
                    <div className="flex flex-wrap gap-4">
                      {(
                        [
                          ["vwfsContactByEmail", "E-mail"],
                          ["vwfsContactByPhone", "Phone Call"],
                          ["vwfsContactBySMS", "SMS"],
                          ["vwfsContactByPost", "Post"],
                        ] as const
                      ).map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 text-[12px]">
                          <Checkbox
                            checked={form[key] === true}
                            onCheckedChange={(checked) => set(key, checked === true)}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </Row>

                  <Row
                    label="Do you have any circumstances, characteristics, or needs that you would like to make Volkswagen Financial Services (UK) Limited aware of that would require additional support?"
                    error={errors.requiresAdditionalSupport}
                  >
                    <YesNo
                      value={form.requiresAdditionalSupport}
                      onChange={(v) =>
                        setForm((prev) => ({
                          ...prev,
                          requiresAdditionalSupport: v,
                          additionalSupportDetails: v ? prev.additionalSupportDetails : null,
                        }))
                      }
                      invalid={!!errors.requiresAdditionalSupport}
                    />
                  </Row>
                  {form.requiresAdditionalSupport === true && (
                    <Row
                      label="Additional Support Details"
                      error={errors.additionalSupportDetails}
                      htmlFor="aq-support-details"
                    >
                      <Input
                        id="aq-support-details"
                        placeholder="Enter details"
                        className={cn(errors.additionalSupportDetails && "border-rose-500")}
                        value={form.additionalSupportDetails ?? ""}
                        onChange={(e) => set("additionalSupportDetails", e.target.value || null)}
                      />
                    </Row>
                  )}

                  <Row
                    label="Do you or your company have a majority shareholding interest in a company based in the UK that has current lending with Volkswagen Financial Services (UK) Limited*?"
                    error={errors.hasMajorityShareholdingInterest}
                  >
                    <YesNo
                      value={form.hasMajorityShareholdingInterest}
                      onChange={handleCompanyToggle("hasMajorityShareholdingInterest")}
                      invalid={!!errors.hasMajorityShareholdingInterest}
                    />
                  </Row>
                  {form.hasMajorityShareholdingInterest === true && (
                    <>
                      {errors.shareholdingCompanies && (
                        <p className="text-[11px] text-rose-600">{errors.shareholdingCompanies}</p>
                      )}
                      <CompanyRows
                        rows={form.shareholdingCompanies}
                        nameLabel="Company registered name"
                        numberLabel="Company registration number"
                        onChange={(rows) => set("shareholdingCompanies", rows)}
                      />
                    </>
                  )}

                  <Row
                    label="Are you a partner of a partnership trading in the UK that has current lending with Volkswagen Financial Services (UK) Limited*?"
                    error={errors.isPartnerOfPartnership}
                  >
                    <YesNo
                      value={form.isPartnerOfPartnership}
                      onChange={handleCompanyToggle("isPartnerOfPartnership")}
                      invalid={!!errors.isPartnerOfPartnership}
                    />
                  </Row>
                  {form.isPartnerOfPartnership === true && (
                    <>
                      {errors.partnershipCompanies && (
                        <p className="text-[11px] text-rose-600">{errors.partnershipCompanies}</p>
                      )}
                      <CompanyRows
                        rows={form.partnershipCompanies}
                        nameLabel="Partnership registered name"
                        numberLabel="Partnership registration number"
                        onChange={(rows) => set("partnershipCompanies", rows)}
                      />
                    </>
                  )}

                  <p className="text-[11px] italic text-muted-foreground">{VWFS_DISCLAIMER}</p>

                  {stringSelect(
                    "vwfsOccupation",
                    "Occupation Sector",
                    vwfsOccupationSectorOptions,
                    "Select an option"
                  )}
                  {showPreviousOccupation &&
                    stringSelect(
                      "vwfsPreviousOccupation",
                      "Previous Occupation Sector",
                      vwfsOccupationSectorOptions,
                      "Select an option"
                    )}

                  <Row error={errors.consentToHardCreditSearch} label="">
                    <label className="flex items-start gap-2 text-[12px] leading-snug">
                      <Checkbox
                        className="mt-0.5"
                        checked={form.consentToHardCreditSearch === true}
                        onCheckedChange={(checked) =>
                          set("consentToHardCreditSearch", checked === true)
                        }
                      />
                      <span>
                        By submitting your application you agree to Volkswagen Financial Services
                        (UK) Limited performing a credit check. Agencies will make a record of
                        checks, this may be available to others and may also affect your ability to
                        borrow from other lenders within a short period. The final credit decision
                        will be subject to your overall status. Do you confirm?
                      </span>
                    </label>
                  </Row>

                  <Row
                    error={errors.agreeToVWFSPrivacyPolicy}
                    label={
                      <>
                        Please confirm you have read and understood Volkswagen Financial Services
                        (UK) Limited&apos;s Privacy Policy.{" "}
                        <a
                          href={VWFS_PRIVACY_POLICY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline underline-offset-2"
                        >
                          View the Privacy Policy
                        </a>
                      </>
                    }
                  >
                    <label className="flex items-center gap-2 text-[12px]">
                      <Checkbox
                        checked={form.agreeToVWFSPrivacyPolicy === true}
                        onCheckedChange={(checked) =>
                          set("agreeToVWFSPrivacyPolicy", checked === true)
                        }
                      />
                      I confirm
                    </label>
                  </Row>
                </Section>
              )}
            </div>
          </>
        )}

        {saveError && (
          <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <span>{saveError}</span>
          </div>
        )}

        {errorCount > 0 && (
          <p className="text-[12px] text-rose-600">
            Please complete the {errorCount} highlighted {errorCount === 1 ? "answer" : "answers"}.
          </p>
        )}
      </div>

      <footer className="flex justify-end gap-3 border-t border-border px-4 py-3 sm:px-6">
        <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
          Back
        </Button>
        <Button
          className="bg-purple-600 text-white hover:bg-purple-700"
          disabled={loading || saving || applicationId == null}
          onClick={handleSubmit}
        >
          {saving && <Loader2 className="mr-1 size-3.5 animate-spin" />}
          Save &amp; Continue
        </Button>
      </footer>
    </div>
  );
}
